import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import ExportModal from "@/components/ExportModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, 
  Filter, 
  Download, 
  Megaphone,
  MapPin,
  Clock,
  ChevronRight
} from "lucide-react";
import { format } from "date-fns";

const STATUS_OPTIONS = ["live_call", "log", "report", "investigating", "arrest", "closed"];

export default function PR() {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportData, setExportData] = useState("");
  const [exportFilename, setExportFilename] = useState("");
  const [exportDownloadUrl, setExportDownloadUrl] = useState("");

  useEffect(() => {
    fetchPRIncidents();
  }, []);

  const fetchPRIncidents = async () => {
    try {
      const response = await axios.get(`${API}/pr-incidents`, { withCredentials: true });
      setIncidents(response.data);
    } catch (error) {
      toast.error("Failed to load PR incidents");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      toast.info("Preparing export...");
      const response = await axios.get(`${API}/export/pr`, {
        withCredentials: true,
        responseType: "text"
      });
      if (!response.data || response.data.includes('"message"')) {
        toast.info("No data to export");
        return;
      }
      setExportData(response.data);
      setExportFilename(`pr_incidents_${format(new Date(), "yyyy-MM-dd")}.csv`);
      setExportDownloadUrl(`${API}/download/pr`);
      setShowExportDialog(true);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed");
    }
  };

  const filteredIncidents = incidents.filter(inc => {
    const matchesSearch = !searchQuery || 
      inc.headline.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inc.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inc.incident_type.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = !filterStatus || inc.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusClass = (status) => `status-badge status-${status}`;
  const getPriorityClass = (priority) => `priority-${priority}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pr-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="w-6 h-6 text-purple-500" />
          <h1 className="text-2xl font-bold uppercase tracking-tight">PR Incidents</h1>
          <Badge variant="outline" className="text-purple-400 border-purple-500">
            {incidents.length} Flagged
          </Badge>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-pr">
          <Download className="w-4 h-4 mr-1" />
          Export PR
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-purple-900/20 border-purple-500/30">
        <CardContent className="p-3 flex items-center gap-3">
          <Megaphone className="w-5 h-5 text-purple-400" />
          <p className="text-sm text-purple-200">
            This page displays all incidents with the PR flag checked. Toggle PR on incident details to add/remove from this list.
          </p>
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search PR incidents..."
                className="pl-10 input-dark"
                data-testid="search-pr"
              />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4 mr-1" />
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-800 flex items-center gap-4">
              <div className="w-48">
                <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                  <SelectTrigger className="input-dark">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s.replace("_", " ").toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="ghost" onClick={() => {
                setSearchQuery("");
                setFilterStatus("");
              }}>
                Clear
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Incidents List */}
      <Card className="bg-slate-900 border-slate-800">
        <ScrollArea className="h-[calc(100vh-22rem)]">
          <table className="data-table">
            <thead className="sticky top-0 bg-slate-900">
              <tr>
                <th>Incident</th>
                <th>Type</th>
                <th>Location</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Date/Time</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredIncidents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500">
                    <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No PR incidents found</p>
                  </td>
                </tr>
              ) : (
                filteredIncidents.map((incident) => (
                  <tr 
                    key={incident.incident_id} 
                    onClick={() => navigate(`/incidents/${incident.incident_id}`)}
                    className="cursor-pointer"
                    data-testid={`pr-incident-${incident.incident_id}`}
                  >
                    <td>
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-purple-400 border-purple-500 text-xs">PR</Badge>
                          <p className="font-medium">{incident.headline}</p>
                        </div>
                        <p className="text-xs text-slate-500 font-mono mt-1">{incident.incident_id}</p>
                      </div>
                    </td>
                    <td>
                      <Badge variant="outline">{incident.incident_type}</Badge>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="w-3 h-3 text-slate-500" />
                        <span className="truncate max-w-48">{incident.address}</span>
                      </div>
                    </td>
                    <td>
                      <span className={getStatusClass(incident.status)}>
                        {incident.status?.replace("_", " ")}
                      </span>
                    </td>
                    <td>
                      <Badge className={getPriorityClass(incident.priority)}>
                        {incident.priority}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm text-slate-400">
                        <Clock className="w-3 h-3" />
                        <span className="font-mono text-xs">
                          {format(new Date(incident.date_time), "MM/dd/yy HH:mm")}
                        </span>
                      </div>
                    </td>
                    <td>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ScrollArea>
      </Card>

      {/* Export Modal */}
      <ExportModal
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        csvData={exportData}
        filename={exportFilename}
        downloadUrl={exportDownloadUrl}
      />
    </div>
  );
}
